// ─── API client ────────────────────────────────────────────────────────────────
// Structured so real endpoints can replace mock handlers by swapping the baseURL
// and removing the mock interceptor.

import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import type { ApiError } from '../types';
import logger from '../utils/logger';
import { getRuntimeEnv } from '../config/runtimeEnv';
import { NETWORK_ERROR_CODE, TIMEOUT_ERROR_CODE } from './transientErrors';

// Runtime config (window.__RUNTIME_CONFIG__) takes precedence so a single built
// image can be promoted across environments — the container regenerates it from
// env vars at startup (see ui/docker-entrypoint.d). Falls back to the build-time
// REACT_APP_API_BASE_URL (.env.local during local dev), then the constant below.

// ── API base URL ─────────────────────────────────────────────────────────────
// To switch environments: comment out one line, uncomment the other.

// DEV (Azure):
const FALLBACK_BASE_URL = 'https://ucarz-api-dev.findcoder.co.uk/api/v1';

// LOCAL (.NET API running on this Mac): 
//const FALLBACK_BASE_URL = 'http://localhost:5025/api/v1';

const BASE_URL = getRuntimeEnv('REACT_APP_API_BASE_URL', FALLBACK_BASE_URL);

// Lowercase the first character of each dot-separated segment in a backend
// property name (e.g. "FirstName" → "firstName", "Address.Line1" →
// "address.line1") so error-details keys line up with Formik field names and
// setFieldError can walk into nested objects via Formik's dot notation.
function lowercaseFirstChar(name: string): string {
  return name
    .split('.')
    .map((seg) => (seg.length > 0 ? seg[0].toLowerCase() + seg.slice(1) : seg))
    .join('.');
}

const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// ── Request interceptor: attach auth token ────────────────────────────────────
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = sessionStorage.getItem('auth_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    logger.debug(`API REQUEST: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    logger.error('API request error', error);
    return Promise.reject(error);
  }
);

// ── Response interceptor: normalise errors ────────────────────────────────────
apiClient.interceptors.response.use(
  (response) => {
    logger.debug(`API RESPONSE: ${response.status} ${response.config.url}`);
    return response;
  },
  (error: AxiosError) => {
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    const apiError: ApiError = {
      message: 'An unexpected error occurred.',
      code: 'UNKNOWN_ERROR',
      statusCode: error.response?.status ?? 0,
    };

    if (error.response) {
      const data = error.response.data as {
        message?: string;
        code?: string;
        details?: Record<string, string[]>;
        errors?: Array<{ code?: string; message?: string; field?: string | null }>;
      };
      apiError.message = data.message ?? error.message;
      apiError.code = data.code ?? `HTTP_${error.response.status}`;

      // Backend (ASP.NET FluentValidation) sends `errors: [{code, message, field}]`
      // with PascalCase field names. Reshape into `details: Record<field, string[]>`
      // keyed by camelCase field names so Formik pages can look up errors by their
      // own field names directly. Non-field errors (field null/undefined) collect
      // under the "_" key so the summary component can surface them.
      if (Array.isArray(data.errors) && data.errors.length > 0) {
        const grouped: Record<string, string[]> = {};
        for (const e of data.errors) {
          const key = e.field ? lowercaseFirstChar(e.field) : '_';
          // Only use the human message. Never surface the raw error code
          // (e.g. "NotEmpty", "MaximumLength") — those are meant for logs.
          const msg = e.message?.trim() || 'Please check this field.';
          (grouped[key] ??= []).push(msg);
        }
        apiError.details = grouped;
      } else if (data.details) {
        apiError.details = data.details;
      }

      if (error.response.status === 401) {
        // Only treat 401 as "session expired, kick back to /login" when we're not already on a
        // public auth page. On /login itself a 401 is just "wrong password / locked account" and
        // the form should keep its error visible — reloading the page would wipe it instantly.
        const path = window.location.pathname;
        const onPublicAuthPage = path === '/login' || path.startsWith('/auth/');
        if (!onPublicAuthPage) {
          sessionStorage.removeItem('auth_token');
          window.location.href = '/login';
        }
      }
    } else if (error.request) {
      // The request left the browser but nothing came back. Two very different
      // causes land here and they used to share one message that blamed the
      // user's connection — which sent operators chasing their own wifi while
      // the real fault was server-side.
      //
      //   * timeout      — the API accepted the request and was still working on
      //                    it when `timeout` above elapsed. The connection is
      //                    fine; the server is slow (or restarting).
      //   * no response  — the connection genuinely failed, or a gateway error
      //                    was blocked by the browser before we could read it
      //                    (an origin 502/503 carries no CORS headers, so it is
      //                    indistinguishable from a dropped connection here).
      //
      // Both are transient and safe to retry — see `isTransientApiError`, which
      // is what the react-query client keys its retry policy off.
      const timedOut = error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT';
      apiError.message = timedOut
        ? 'The server took too long to respond. Please try again.'
        : 'Could not reach the server. Retrying — please try again in a moment.';
      apiError.code = timedOut ? TIMEOUT_ERROR_CODE : NETWORK_ERROR_CODE;
    }

    logger.error('API error', apiError);
    return Promise.reject(apiError);
  }
);

export default apiClient;
